import { useEffect, useRef, useState } from "react";
import logoWhite from "../../../img/logo_white.png";
import "./BodhiLoader.css";

/**
 * Bodhi brand loader — a spinning Bodhi logo. Pure CSS, no antd.
 *
 * Use it anywhere a loading state is needed: boot splash, full-page
 * overlays, table/section placeholders, cards, etc.
 *
 * Props:
 *   size               {number}  Logo size in px (default 80)
 *   mode               {"fullscreen"|"overlay"|"inline"|"bare"}
 *                        "fullscreen" – fixed, covers the viewport (boot / route loading)
 *                        "overlay"    – absolute, covers the nearest positioned parent
 *                        "inline"     – flows in layout, centered (tables / sections)
 *                        "bare"       – just the spinning logo, no container/backdrop
 *                                       (tiny inline indicators, e.g. sidebar rows)
 *   backgroundOpacity  {number}  0–1 opacity for the backdrop (default 0.85; 0 = transparent)
 *   ready              {boolean} When true + minDuration elapsed, fades out and calls onHide
 *   onHide             {func}    Called after the fade-out finishes
 *   minDurationSeconds {number}  Minimum visible time in seconds (default 0)
 *   className          {string}  Extra class names
 *   style              {object}  Extra inline styles (merged last)
 */
const BodhiLoader = ({
	size = 80,
	mode = "overlay",
	backgroundOpacity = 0.85,
	ready = false,
	onHide,
	minDurationSeconds = 0,
	className = "",
	style,
}) => {
	const [visible, setVisible] = useState(true);
	const [fading, setFading] = useState(false);

	const minElapsed = useRef(false);
	const isReady = useRef(false);

	const tryFadeOut = () => {
		if (minElapsed.current && isReady.current && !fading) {
			setFading(true);
		}
	};

	// Minimum duration timer
	useEffect(() => {
		if (minDurationSeconds <= 0) {
			minElapsed.current = true;
			tryFadeOut();
			return;
		}
		const timer = setTimeout(() => {
			minElapsed.current = true;
			tryFadeOut();
		}, minDurationSeconds * 1000);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// React to ready prop
	useEffect(() => {
		if (ready) {
			isReady.current = true;
			tryFadeOut();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ready]);

	// After fade transition ends, unmount and call onHide
	const handleTransitionEnd = () => {
		if (fading) {
			setVisible(false);
			onHide?.();
		}
	};

	if (!visible) return null;

	// "bare" — just the spinning logo, no container/backdrop/min-height.
	if (mode === "bare") {
		return (
			<img
				className={`bodhi-loader__logo${className ? ` ${className}` : ""}`}
				src={logoWhite}
				alt="Loading…"
				style={{ width: size, height: size, ...style }}
			/>
		);
	}

	return (
		<div
			className={`bodhi-loader bodhi-loader--${mode}${
				fading ? " bodhi-loader--fading" : ""
			}${className ? ` ${className}` : ""}`}
			style={{
				"--bodhi-loader-size": `${size}px`,
				"--bodhi-loader-bg-opacity": backgroundOpacity,
				...style,
			}}
			onTransitionEnd={handleTransitionEnd}
		>
			<img className="bodhi-loader__logo" src={logoWhite} alt="Loading…" />
		</div>
	);
};

export default BodhiLoader;
